import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Flex, HStack, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalHeader, ModalOverlay, Spinner, Text, VStack,
} from '@chakra-ui/react';
import {
  CreditPack, SubPlan,
  useGetCreditPacksQuery, useGetSubPlansQuery,
  useCreateBuyCreditsSessionMutation, useInitCheckoutMutation,
  useGetBillingPortalUrlMutation, useGetCurrentSubscriptionQuery,
} from '../redux/services/mixgenie';

/**
 * PaywallModal — mixgenie port of app.pictools.ai's "Get more credits"
 * pricing sheet. Opens from any 402/INSUFFICIENT_CREDITS point in the app
 * (song-gen, stem-split, stems-from-upload), the credits chip when the
 * balance is zero, or the on-demand "Buy credits" CTA elsewhere.
 *
 * Shape (deliberately mirrors pictools so UX muscle memory transfers):
 *   - Two tabs: Credit Packs | Pro Subscription
 *   - Each panel is a row-list of selectable rows
 *   - One CTA at the bottom that reflects the current selection
 *     ("Buy N credits for $X" / "Get Pro for $X/mo") → Stripe checkout
 *
 * Billing plumbing is the shared platform (main-service): catalogs via
 * useGetCreditPacksQuery/useGetSubPlansQuery, checkout via
 * useCreateBuyCreditsSessionMutation + useInitCheckoutMutation. We never
 * speak to Stripe directly — just redirect to the session URL main-service
 * returns, same as pictools.
 *
 * `reason` is threaded for future funnel tracking (which paywall entry
 * point drove the purchase). Not instrumented yet but shaped the same way.
 */
export type PaywallReason =
  | 'nocredits'          // user hit 402 mid-action
  | 'credits-chip'       // tapped the credits chip in the header
  | 'post-result'        // just finished a generation
  | 'open';              // bare open (e.g., Buy credits link)

const CURRENCY_SYMBOL: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };
function money(cost: number, currency = 'USD'): string {
  const sym = CURRENCY_SYMBOL[currency.toUpperCase()] || currency.toUpperCase() + ' ';
  const n = cost % 1 === 0 ? String(cost) : cost.toFixed(2);
  return `${sym}${n}`;
}
function perCreditLabel(cost: number, credits: number, currency = 'USD'): string {
  if (!credits) return '';
  const per = cost / credits;
  return `${money(per, currency)}/credit`;
}

export function PaywallModal({
  isOpen,
  onClose,
  reason = 'open',
}: {
  isOpen: boolean;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  reason?: PaywallReason;
}) {
  const [tab, setTab] = useState<'packs' | 'pro'>('packs');
  const [cycle, setCycle] = useState<'monthly' | 'annual'>('monthly');
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const { data: packs, isLoading: packsLoading } = useGetCreditPacksQuery(undefined as any, { skip: !isOpen });
  const { data: plans, isLoading: plansLoading } = useGetSubPlansQuery(undefined as any, { skip: !isOpen });
  const { data: currentSub } = useGetCurrentSubscriptionQuery(undefined as any, { skip: !isOpen });
  const [createBuy, { isLoading: buyLoading }] = useCreateBuyCreditsSessionMutation();
  const [initSub, { isLoading: subLoading }] = useInitCheckoutMutation();
  const [openPortal, { isLoading: portalLoading }] = useGetBillingPortalUrlMutation();
  const isCheckingOut = buyLoading || subLoading || portalLoading;

  // Sort packs by credits ASC and pick a sensible default (middle = "Most chosen").
  const sortedPacks = useMemo<CreditPack[]>(() => {
    const arr = (packs || []).slice().sort((a, b) => a.amount - b.amount);
    return arr;
  }, [packs]);
  const popularPackId = sortedPacks[Math.floor(sortedPacks.length / 2)]?._id || null;

  const sortedPlans = useMemo<SubPlan[]>(() => {
    const arr = (plans || []).slice().sort((a, b) => a.nTokens - b.nTokens);
    return arr;
  }, [plans]);
  const popularPlanId = sortedPlans[Math.floor(sortedPlans.length / 2)]?._id || null;

  // Keep the current selection valid when the catalog arrives / changes.
  useEffect(() => {
    if (!sortedPacks.length) return;
    if (!selectedPackId || !sortedPacks.some((p) => p._id === selectedPackId)) {
      setSelectedPackId(popularPackId || sortedPacks[0]._id);
    }
  }, [sortedPacks, selectedPackId, popularPackId]);
  useEffect(() => {
    if (!sortedPlans.length) return;
    if (!selectedPlanId || !sortedPlans.some((p) => p._id === selectedPlanId)) {
      setSelectedPlanId(popularPlanId || sortedPlans[0]._id);
    }
  }, [sortedPlans, selectedPlanId, popularPlanId]);

  // Baseline per-credit price for the smallest pack — used to compute "Save X%" on larger packs.
  const baselinePerCredit = sortedPacks[0] ? sortedPacks[0].cost / sortedPacks[0].amount : 0;
  const packCurrency = sortedPacks[0]?.currency || 'USD';

  const activePack = sortedPacks.find((p) => p._id === selectedPackId) || null;
  const activePlan = sortedPlans.find((p) => p._id === selectedPlanId) || null;

  const isPaidSub = !!(currentSub && (currentSub as any).data?.subscription?.status === 'active');
  const activeSubLabel = (currentSub as any)?.data?.subscription?.plan?.name || 'Your plan';

  async function startPackCheckout() {
    if (!activePack) return;
    setCheckoutError(null);
    try {
      const priceId = activePack.metadata?.subscription?.priceID;
      if (!priceId) {
        setCheckoutError('This pack is missing a Stripe price. Please pick another.');
        return;
      }
      const returnUrl = `${window.location.origin}/credits?billing=success`;
      const res: any = await createBuy({
        priceId,
        creditAmount: activePack.amount,
        returnUrl,
      }).unwrap();
      const url = res?.url || res?.data?.url;
      if (url) { window.location.href = url; return; }
      setCheckoutError('Could not start checkout. Please try again.');
    } catch (err: any) {
      setCheckoutError(err?.data?.message || 'Checkout failed.');
    }
  }

  async function startSubCheckout() {
    if (!activePlan) return;
    setCheckoutError(null);
    try {
      const successUrl = `${window.location.origin}/credits?billing=success`;
      const cancelUrl = `${window.location.origin}/credits?billing=cancel`;
      const res: any = await initSub({
        planID: activePlan._id,
        billingCycle: cycle,
        successUrl,
        cancelUrl,
      }).unwrap();
      const url = res?.url || res?.data?.url;
      if (url) { window.location.href = url; return; }
      setCheckoutError('Could not start checkout. Please try again.');
    } catch (err: any) {
      setCheckoutError(err?.data?.message || 'Checkout failed.');
    }
  }

  async function openStripePortal() {
    setCheckoutError(null);
    try {
      const res: any = await openPortal().unwrap();
      const url = res?.url || res?.data?.url;
      if (url) { window.location.href = url; return; }
      setCheckoutError("Couldn't open the billing portal.");
    } catch (err: any) {
      setCheckoutError(err?.data?.message || 'Billing portal failed.');
    }
  }

  const loadingCatalog = packsLoading || plansLoading;
  const nocreditsBanner = reason === 'nocredits';

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="mg.surface" color="mg.fg" borderRadius={{ base: 0, md: '16px' }} maxH={{ base: '100vh', md: '90vh' }}>
        <ModalHeader textAlign="center" pt={6} pb={2}>
          <Text fontSize="24px" fontWeight={700}>Get more credits</Text>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />
        <ModalBody pb={6}>
          <Text fontSize="sm" color="mg.muted" textAlign="center" mb={5}>
            1 credit = 1 AI creation. Credits <b>never expire</b> — top up once and keep creating.
            Bigger packs cost less per credit.
          </Text>

          {/* Tab switcher (mirrors pictools' .pricing-tabs) */}
          <Flex
            borderRadius="full"
            bg="mg.surfaceLo"
            p="4px"
            mb={5}
          >
            {([['packs', 'Credit Packs'], ['pro', 'Pro Subscription']] as const).map(([k, label]) => (
              <Box
                key={k}
                as="button"
                flex="1"
                py={2.5}
                borderRadius="full"
                fontSize="14px"
                fontWeight={600}
                bg={tab === k ? 'mg.surface' : 'transparent'}
                color={tab === k ? 'mg.fg' : 'mg.muted'}
                boxShadow={tab === k ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'}
                onClick={() => setTab(k)}
                transition="all 120ms"
              >
                {label}
              </Box>
            ))}
          </Flex>

          {nocreditsBanner && (
            <Box
              mb={4}
              px={3}
              py={2.5}
              bg="orange.50"
              color="orange.800"
              borderRadius="12px"
              fontSize="13.5px"
              textAlign="center"
              sx={{ '[data-theme="dark"] &': { bg: 'rgba(255,170,80,0.12)', color: 'orange.200' } }}
            >
              You're out of credits — top up or go Pro to keep creating.
            </Box>
          )}

          {loadingCatalog && (
            <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
          )}

          {!loadingCatalog && tab === 'packs' && (
            <Box
              border="1px solid"
              borderColor="mg.line"
              borderRadius="14px"
              p={5}
            >
              <Text fontSize="18px" fontWeight={700} color="mg.fg">Credit Packs</Text>
              <Text fontSize="13.5px" fontWeight={600} color="blue.500" mt={1}>
                One-time purchase · Credits never expire
              </Text>
              <Text fontSize="13.5px" color="mg.muted" mt={1} mb={4}>
                For occasional edits and one-off projects.
              </Text>

              <VStack spacing={2} align="stretch">
                {sortedPacks.map((p) => {
                  const isSelected = p._id === selectedPackId;
                  const pcPrice = p.cost / p.amount;
                  const save = baselinePerCredit ? Math.round((1 - pcPrice / baselinePerCredit) * 100) : 0;
                  const isPopular = p._id === popularPackId;
                  const badge = isPopular ? 'Most chosen' : (save >= 5 ? `Save ${save}%` : '');
                  const badgeColor = isPopular ? 'blue' : 'green';
                  return (
                    <PriceRow
                      key={p._id}
                      selected={isSelected}
                      onSelect={() => setSelectedPackId(p._id)}
                      topLeft={`${p.amount} credits`}
                      topRight={money(p.cost, p.currency)}
                      bottomLeft={perCreditLabel(p.cost, p.amount, p.currency)}
                      badge={badge}
                      badgeColor={badgeColor}
                    />
                  );
                })}
              </VStack>

              <Button
                onClick={startPackCheckout}
                mt={4}
                w="100%"
                size="lg"
                bg="blue.600"
                color="white"
                _hover={{ bg: 'blue.700' }}
                borderRadius="12px"
                isLoading={isCheckingOut}
                isDisabled={!activePack}
              >
                {activePack
                  ? `Buy ${activePack.amount} credits for ${money(activePack.cost, activePack.currency)}`
                  : 'Buy credits'}
              </Button>
              <Text fontSize="12px" color="mg.muted" textAlign="center" mt={2}>
                Pay once. Use them whenever you need.
              </Text>
            </Box>
          )}

          {!loadingCatalog && tab === 'pro' && (
            <Box
              border="1px solid"
              borderColor="mg.line"
              borderRadius="14px"
              p={5}
            >
              <Text fontSize="18px" fontWeight={700} color="mg.fg">Pro Subscription</Text>
              {isPaidSub ? (
                <>
                  <Text fontSize="13.5px" color="mg.muted" mt={1} mb={4}>Your plan</Text>
                  <PriceRow
                    selected
                    onSelect={() => undefined}
                    topLeft={activeSubLabel}
                    topRight="Active"
                    bottomLeft="Managed in Stripe"
                  />
                  <Button
                    onClick={openStripePortal}
                    mt={4}
                    w="100%"
                    size="lg"
                    bg="blue.600"
                    color="white"
                    _hover={{ bg: 'blue.700' }}
                    borderRadius="12px"
                    isLoading={isCheckingOut}
                  >
                    Manage billing
                  </Button>
                </>
              ) : (
                <>
                  <Text fontSize="13.5px" color="mg.muted" mt={1} mb={4}>
                    For regular creating · Monthly or annual billing
                  </Text>

                  <HStack
                    spacing={0}
                    bg="mg.surfaceLo"
                    borderRadius="full"
                    p="4px"
                    mb={4}
                  >
                    {(['monthly', 'annual'] as const).map((c) => (
                      <Box
                        key={c}
                        as="button"
                        flex="1"
                        py={2}
                        borderRadius="full"
                        fontSize="13px"
                        fontWeight={600}
                        bg={cycle === c ? 'mg.surface' : 'transparent'}
                        color={cycle === c ? 'mg.fg' : 'mg.muted'}
                        boxShadow={cycle === c ? '0 1px 2px rgba(0,0,0,0.06)' : 'none'}
                        onClick={() => setCycle(c)}
                      >
                        {c === 'monthly' ? 'Monthly' : 'Annual'}
                      </Box>
                    ))}
                  </HStack>

                  <VStack spacing={2} align="stretch">
                    {sortedPlans.map((p) => {
                      const isSelected = p._id === selectedPlanId;
                      const monthlyCost = p.metadata?.subscription?.monthly?.cost || 0;
                      const annualCost = p.metadata?.subscription?.annual?.cost || 0;
                      const monthlyEq = cycle === 'annual' && annualCost ? annualCost / 12 : monthlyCost;
                      const annualSave = (monthlyCost && annualCost)
                        ? Math.round((1 - (annualCost / 12) / monthlyCost) * 100)
                        : 0;
                      const isPopular = p._id === popularPlanId;
                      const badge = isPopular
                        ? 'Most popular'
                        : (cycle === 'annual' && annualSave >= 5 ? `Save ${annualSave}%` : '');
                      const badgeColor = isPopular ? 'blue' : 'green';
                      const subLabel = cycle === 'annual' && annualCost
                        ? `billed ${money(annualCost, 'USD')}/yr`
                        : 'Refreshed monthly';
                      return (
                        <PriceRow
                          key={p._id}
                          selected={isSelected}
                          onSelect={() => setSelectedPlanId(p._id)}
                          topLeft={`${p.nTokens} credits/mo`}
                          topRight={<>
                            {money(monthlyEq, 'USD')}
                            <Text as="span" fontSize="12px" color="mg.muted" fontWeight={500}> /mo</Text>
                          </>}
                          bottomLeft={`${money(monthlyEq / Math.max(1, p.nTokens), 'USD')}/credit · ${subLabel}`}
                          badge={badge}
                          badgeColor={badgeColor}
                        />
                      );
                    })}
                  </VStack>

                  <Button
                    onClick={startSubCheckout}
                    mt={4}
                    w="100%"
                    size="lg"
                    bg="blue.600"
                    color="white"
                    _hover={{ bg: 'blue.700' }}
                    borderRadius="12px"
                    isLoading={isCheckingOut}
                    isDisabled={!activePlan}
                  >
                    {activePlan
                      ? `Get Pro for ${money(
                          cycle === 'annual' && activePlan.metadata?.subscription?.annual?.cost
                            ? (activePlan.metadata.subscription.annual.cost / 12)
                            : (activePlan.metadata?.subscription?.monthly?.cost || 0),
                          'USD'
                        )}/mo`
                      : 'Get Pro'}
                  </Button>
                  <Text fontSize="12px" color="mg.muted" textAlign="center" mt={2}>
                    Renews {cycle === 'annual' ? 'yearly' : 'monthly'} · Cancel anytime
                  </Text>
                </>
              )}
            </Box>
          )}

          {checkoutError && (
            <Text mt={3} fontSize="13px" color="red.500" textAlign="center">{checkoutError}</Text>
          )}

          <VStack mt={5} spacing={1.5} fontSize="12px" color="mg.muted" align="stretch">
            <Text>🔒  Secure checkout by Stripe · credits land instantly · cancel anytime.</Text>
            <Text>🛡  Every generation runs through our safety filters.</Text>
            <Text>🎧  Your songs stay private to your account — deletable anytime.</Text>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function PriceRow({
  selected,
  onSelect,
  topLeft,
  topRight,
  bottomLeft,
  badge,
  badgeColor = 'blue',
}: {
  selected: boolean;
  onSelect: () => void;
  topLeft: React.ReactNode;
  topRight: React.ReactNode;
  bottomLeft: React.ReactNode;
  badge?: string;
  badgeColor?: 'blue' | 'green';
}) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onSelect}
      textAlign="left"
      w="100%"
      px={4}
      py={3.5}
      borderRadius="12px"
      border="2px solid"
      borderColor={selected ? 'blue.500' : 'mg.line'}
      bg={selected ? 'blue.50' : 'mg.surface'}
      sx={{
        '[data-theme="dark"] &': {
          bg: selected ? 'rgba(66,153,225,0.12)' : 'mg.surface',
        },
      }}
      transition="all 120ms"
      _hover={{ borderColor: selected ? 'blue.500' : 'mg.line2' }}
    >
      <Flex justify="space-between" align="center">
        <Text fontSize="16px" fontWeight={700}>{topLeft}</Text>
        <Text fontSize="22px" fontWeight={800}>{topRight}</Text>
      </Flex>
      <Flex justify="space-between" align="center" mt={1}>
        <Text fontSize="13px" color="mg.muted">{bottomLeft}</Text>
        {badge && (
          <Box
            px={2.5}
            py={0.5}
            borderRadius="full"
            fontSize="11.5px"
            fontWeight={700}
            bg={badgeColor === 'blue' ? 'blue.100' : 'green.100'}
            color={badgeColor === 'blue' ? 'blue.700' : 'green.700'}
            sx={{
              '[data-theme="dark"] &': {
                bg: badgeColor === 'blue' ? 'rgba(66,153,225,0.18)' : 'rgba(104,211,145,0.18)',
                color: badgeColor === 'blue' ? 'blue.200' : 'green.200',
              },
            }}
          >
            {badge}
          </Box>
        )}
      </Flex>
    </Box>
  );
}
